import { z } from 'zod';

import { NOTIFICATION_KINDS, type NotificationKind } from './notifications.dto';

/**
 * Phase 6 — per-user notification preferences (the matrix UI on
 * `/me/notifications`).  See migration 0032 for the storage shape.
 */

// ── Groups ──────────────────────────────────────────────────────────────────

export const NOTIFICATION_GROUPS = ['team', 'project', 'personal', 'chat'] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

/**
 * Canonical mapping from each existing NotificationKind to its UX group.
 * The settings UI surfaces groups; under the hood, every fanout passes through
 * the kind → group lookup before consulting per-user preferences.
 *
 * `chat` is intentionally empty for now — Phase 6 ships the prefs surface;
 * chat fanout joins later via a `'chat_*'` kind family in a follow-up slice.
 */
export const NOTIFICATION_KIND_GROUP: Record<NotificationKind, NotificationGroup> = {
  // team
  added_to_team: 'team',
  role_changed: 'team',
  team_invitation: 'team',
  // project
  added_to_project: 'project',
  // personal
  mention: 'personal',
  assignment: 'personal',
  reply: 'personal',
  due_soon: 'personal',
  overdue: 'personal',
  reminder: 'personal',
  status_changed: 'personal',
  watcher_added: 'personal',
  system: 'personal',
  ai_suggestion: 'personal',
  // chat — Phase 6 follow-up.  Producers wired in FanoutService 2026-05-16.
  chat_mention: 'chat',
  chat_thread_reply: 'chat',
  chat_reaction: 'chat',
  chat_message_in_subscribed_topic: 'chat',
  // Phase 4 polish — Approval Center inbox fanout (migration 0074).
  // Both grouped under 'personal' so the existing preferences matrix
  // accepts them without UI changes (admins can mute approval inbox
  // entries via the same `personal` toggle they already see).
  approval_pending: 'personal',
  approval_resolved: 'personal',
  // Sprint 1.3 — stranger DM request lands in the personal inbox.
  message_request: 'personal',
  // W3.2 — a reply in an existing DM thread; same 'personal' group as the
  // request so users mute both with the toggle they already have.
  dm_message: 'personal',
};

// ── Transport bitmap ────────────────────────────────────────────────────────

export const TRANSPORT_INBOX = 1;
export const TRANSPORT_EMAIL = 2;
export const TRANSPORT_PUSH = 4;
export const TRANSPORT_ALL = TRANSPORT_INBOX | TRANSPORT_EMAIL | TRANSPORT_PUSH;

export interface TransportSet {
  inbox: boolean;
  email: boolean;
  push: boolean;
}

export function maskToTransports(mask: number): TransportSet {
  return {
    inbox: (mask & TRANSPORT_INBOX) === TRANSPORT_INBOX,
    email: (mask & TRANSPORT_EMAIL) === TRANSPORT_EMAIL,
    push: (mask & TRANSPORT_PUSH) === TRANSPORT_PUSH,
  };
}

export function transportsToMask(t: TransportSet): number {
  return (
    (t.inbox ? TRANSPORT_INBOX : 0) |
    (t.email ? TRANSPORT_EMAIL : 0) |
    (t.push ? TRANSPORT_PUSH : 0)
  );
}

/** Out-of-the-box policy used when the user has no overrides for the
 *  resolved (group, scope) combination.  Tuned to be loud-but-not-spammy:
 *  inbox is always on (folder-style discoverability); email reserved for
 *  team invites + personal mentions; push for direct + chat. */
export const DEFAULT_TRANSPORT_BY_GROUP: Record<NotificationGroup, TransportSet> = {
  team: { inbox: true, email: true, push: false },
  project: { inbox: true, email: false, push: false },
  personal: { inbox: true, email: true, push: true },
  chat: { inbox: true, email: false, push: true },
};

// ── DTOs ────────────────────────────────────────────────────────────────────

export const NotificationPreferenceScopeSchema = z.enum(['global', 'team', 'event']);
export type NotificationPreferenceScope = z.infer<typeof NotificationPreferenceScopeSchema>;

export const NotificationPreferenceRowSchema = z.object({
  scopeKind: NotificationPreferenceScopeSchema,
  /** team uuid when scopeKind='team', null otherwise. */
  scopeId: z.string().uuid().nullable(),
  /** Group name or a specific NotificationKind. */
  eventKey: z.string(),
  transportMask: z.number().int().min(0).max(7),
  muted: z.boolean(),
  updatedAt: z.string().datetime(),
});
export type NotificationPreferenceRowDto = z.infer<typeof NotificationPreferenceRowSchema>;

/**
 * Composite read-model returned by GET /api/me/notification-preferences.
 * The frontend renders the matrix directly from this shape; no joins
 * required at render time.
 */
export const NotificationPreferencesSchema = z.object({
  /** Resolved per-group default-or-override at the global scope. */
  groupDefaults: z.record(NotificationPreferenceScopeSchema, z.unknown()).optional(), // placeholder, populated client-side
  /** All persisted override rows for the actor. */
  overrides: z.array(NotificationPreferenceRowSchema),
});
export type NotificationPreferencesProjection = z.infer<typeof NotificationPreferencesSchema>;

// ── Mutations ───────────────────────────────────────────────────────────────

export const UpsertNotificationPreferenceSchema = z.object({
  scopeKind: NotificationPreferenceScopeSchema,
  scopeId: z.string().uuid().nullable(),
  eventKey: z.string().min(1).max(64),
  transportMask: z.number().int().min(0).max(7),
  muted: z.boolean(),
});
export type UpsertNotificationPreferenceInput = z.infer<typeof UpsertNotificationPreferenceSchema>;

export const DeleteNotificationPreferenceSchema = z.object({
  scopeKind: NotificationPreferenceScopeSchema,
  scopeId: z.string().uuid().nullable(),
  eventKey: z.string().min(1).max(64),
});
export type DeleteNotificationPreferenceInput = z.infer<typeof DeleteNotificationPreferenceSchema>;

// Convenience constant — full kinds list as a Zod enum for runtime guard
// when validating user-supplied eventKey strings.
export const ALL_EVENT_KEYS = [
  ...NOTIFICATION_GROUPS,
  ...NOTIFICATION_KINDS,
] as const;
export const EventKeySchema = z.enum(ALL_EVENT_KEYS);
